/**
 * Form model for the Straddle Simulator.
 *
 * A straddle has two legs with a strike distance and an expiration each, so
 * crossing everything explodes: 3 distances and 3 tenors per leg is 81 pairs per
 * ticker, most of them unwanted. So the form works in explicit PAIRS instead: pick
 * the values you care about, choose which shapes of pairing to include, then click
 * any single cell in the grid to add or drop it. Strikes and expirations share one
 * model below.
 *
 * TENORS, STRIKE_DISTANCES and the caps mirror the backend's
 * stock_analyzer/services/straddles/constants.py so limits show before a request
 * is sent. test_straddle_selection.py reads this file and fails if they drift, so
 * keep each table entry on one line in this format.
 */
import { formatTickersForAPI } from './presetMappings';
import { formatMoney } from './straddleCalculations';

export const MAX_TICKERS = 10;
export const MAX_DISTANCES = 6;
export const MAX_TENORS = 6;
export const MAX_ROWS = 500;

export const TENORS = [
  { key: '1W', label: '1 Week', target: 7, min: 4, max: 10 },
  { key: '2W', label: '2 Weeks', target: 14, min: 11, max: 18 },
  { key: '1M', label: '1 Month', target: 30, min: 19, max: 45 },
  { key: '2M', label: '2 Months', target: 60, min: 46, max: 75 },
  { key: '3M', label: '3 Months', target: 90, min: 76, max: 120 },
  { key: '6M', label: '6 Months', target: 180, min: 121, max: 225 },
  { key: '1Y', label: '1 Year', target: 365, min: 226, max: 450 },
  { key: '18M+', label: '18 Months+', target: 545, min: 451, max: 1010 },
];

export const STRIKE_DISTANCES = [
  { value: 0, label: 'ATM' },
  { value: 0.025, label: '2.5%' },
  { value: 0.05, label: '5%' },
  { value: 0.1, label: '10%' },
  { value: 0.15, label: '15%' },
  { value: 0.2, label: '20%' },
  { value: 0.3, label: '30%' },
];

// ---------------------------------------------------------------------------
// Pair grids
// ---------------------------------------------------------------------------

/** Regions of a pair grid. Axes run nearest to farthest, so a higher index is "more". */
export const SHAPE_EVEN = 'even';
export const SHAPE_CALL = 'call';
export const SHAPE_PUT = 'put';

export const STRIKE_SHAPES = [
  { key: SHAPE_EVEN, label: 'Even', tooltip: 'The call and the put sit the same distance from the stock.' },
  { key: SHAPE_CALL, label: 'Call farther', tooltip: 'The call sits farther out than the put: a downside lean.' },
  { key: SHAPE_PUT, label: 'Put farther', tooltip: 'The put sits farther out than the call: an upside lean.' },
];

export const EXPIRY_SHAPES = [
  { key: SHAPE_EVEN, label: 'Same date', tooltip: 'Both legs expire on the same date.' },
  { key: SHAPE_CALL, label: 'Call later', tooltip: 'The call outlives the put.' },
  { key: SHAPE_PUT, label: 'Put later', tooltip: 'The put outlives the call.' },
];

export const cellKey = (callValue, putValue) => `${callValue}|${putValue}`;

/** Which shape a cell belongs to, from its position on each axis. */
export const cellShape = (callIndex, putIndex) => {
  if (callIndex === putIndex) return SHAPE_EVEN;
  return callIndex > putIndex ? SHAPE_CALL : SHAPE_PUT;
};

/** A cell is on if its own override says so, otherwise if its shape is on. */
export const isCellOn = (axis, callValue, putValue, shapes, overrides) => {
  const key = cellKey(callValue, putValue);
  if (overrides && key in overrides) return overrides[key];
  return shapes.has(cellShape(axis.indexOf(callValue), axis.indexOf(putValue)));
};

/** Every [callValue, putValue] that is on, in grid order. */
export const selectedPairs = (axis, shapes, overrides) => {
  const pairs = [];
  axis.forEach((callValue) => {
    axis.forEach((putValue) => {
      if (isCellOn(axis, callValue, putValue, shapes, overrides)) pairs.push([callValue, putValue]);
    });
  });
  return pairs;
};

/**
 * Turn a shape on or off. This fills or clears its whole region, so one-off clicks
 * inside that region are discarded rather than left behind.
 */
export const toggleShape = (axis, shapes, overrides, shape) => {
  const nextShapes = new Set(shapes);
  if (nextShapes.has(shape)) nextShapes.delete(shape);
  else nextShapes.add(shape);

  const nextOverrides = { ...overrides };
  axis.forEach((callValue, callIndex) => {
    axis.forEach((putValue, putIndex) => {
      if (cellShape(callIndex, putIndex) === shape) delete nextOverrides[cellKey(callValue, putValue)];
    });
  });
  return { shapes: nextShapes, overrides: nextOverrides };
};

/** Flip one cell. An override matching its shape's own state is simply removed. */
export const toggleCell = (axis, shapes, overrides, callValue, putValue) => {
  const key = cellKey(callValue, putValue);
  const next = !isCellOn(axis, callValue, putValue, shapes, overrides);
  const shapeOn = shapes.has(cellShape(axis.indexOf(callValue), axis.indexOf(putValue)));
  const nextOverrides = { ...overrides };
  if (next === shapeOn) delete nextOverrides[key];
  else nextOverrides[key] = next;
  return nextOverrides;
};

// ---------------------------------------------------------------------------
// Run
// ---------------------------------------------------------------------------

export const countStraddles = (tickerCount, strikePairCount, expiryPairCount) => (
  tickerCount * strikePairCount * expiryPairCount
);

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** The run bar's sentence, e.g. "3 tickers x 3 strike pairs x 6 expiry pairs = 54 straddles". */
export const describeRun = (tickerCount, strikePairCount, expiryPairCount) => (
  `${plural(tickerCount, 'ticker')} x ${plural(strikePairCount, 'strike pair')} x `
  + `${plural(expiryPairCount, 'expiry pair')} = `
  + plural(countStraddles(tickerCount, strikePairCount, expiryPairCount), 'straddle')
);

/** What's stopping a run, naming the input or the limit. Null when it can go. */
export const blockedReason = (tickerCount, strikePairCount, expiryPairCount) => {
  if (tickerCount === 0) return 'Select at least one ticker';
  if (strikePairCount === 0) return 'Select at least one strike pair';
  if (expiryPairCount === 0) return 'Select at least one expiry pair';
  if (tickerCount > MAX_TICKERS) return `Maximum is ${MAX_TICKERS} tickers`;
  const rows = countStraddles(tickerCount, strikePairCount, expiryPairCount);
  if (rows > MAX_ROWS) return `Maximum is ${MAX_ROWS} straddles per run. Remove some tickers or pairs.`;
  return null;
};

/** The API payload. Pairs are [callValue, putValue], as selectedPairs returns them. */
export const buildStraddlePayload = (tickers, strikePairs, expiryPairs) => ({
  tickers: formatTickersForAPI(tickers),
  strike_pairs: strikePairs.map(([call_otm, put_otm]) => ({ call_otm, put_otm })),
  expiry_pairs: expiryPairs.map(([call_tenor, put_tenor]) => ({ call_tenor, put_tenor })),
});

// ---------------------------------------------------------------------------
// Position size
// ---------------------------------------------------------------------------

/**
 * A total plus any number of call/put splits, each giving every straddle its own
 * row. `custom` is one more split, in dollars instead of percentages, so amounts
 * like $700 / $250 can sit beside the presets.
 *
 * Amounts hold raw input while typing, so they can briefly be '' or a string.
 */
export const CUSTOM_SPLIT = 'custom';

export const DEFAULT_SIZING = {
  enabled: false,
  total: 1000,
  splits: ['50-50'],
  customCall: 500,
  customPut: 500,
};

export const SPLIT_PRESETS = [
  { key: '50-50', call: 50, put: 50 },
  { key: '25-75', call: 25, put: 75 },
  { key: '75-25', call: 75, put: 25 },
  { key: '30-70', call: 30, put: 70 },
  { key: '70-30', call: 70, put: 30 },
];

export const splitLabel = (preset) => `${preset.call}/${preset.put}`;

const roundCents = (value) => Math.round(value * 100) / 100;

/** A typed amount as a number, counting anything unusable as nothing. */
const amount = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

export const toggleSplit = (sizing, key) => ({
  ...sizing,
  splits: sizing.splits.includes(key)
    ? sizing.splits.filter((k) => k !== key)
    : [...sizing.splits, key],
});

export const setBudgetTotal = (sizing, raw) => ({ ...sizing, total: raw });

export const setCustomBudget = (sizing, side, raw) => ({
  ...sizing,
  [side === 'call' ? 'customCall' : 'customPut']: raw,
});

/**
 * Every split that can be priced right now, in button order whatever order they
 * were clicked, so the rows don't reshuffle. The put takes the remainder, so a
 * split's sides always add up to the total.
 */
export const resolveBudgets = (sizing) => {
  if (!sizing?.enabled) return [];
  const total = amount(sizing.total);
  const budgets = [];
  if (total > 0) {
    SPLIT_PRESETS.filter((preset) => sizing.splits.includes(preset.key)).forEach((preset) => {
      const callBudget = roundCents((total * preset.call) / 100);
      budgets.push({
        enabled: true,
        key: preset.key,
        label: splitLabel(preset),
        callBudget,
        putBudget: roundCents(total - callBudget),
      });
    });
  }
  if (sizing.splits.includes(CUSTOM_SPLIT) && amount(sizing.customCall) > 0 && amount(sizing.customPut) > 0) {
    budgets.push({
      enabled: true,
      key: CUSTOM_SPLIT,
      label: 'Custom',
      callBudget: amount(sizing.customCall),
      putBudget: amount(sizing.customPut),
    });
  }
  return budgets;
};

/** What's keeping a selected split from being priced, or null if nothing is. */
export const sizingProblem = (sizing) => {
  if (!sizing.enabled) return null;
  if (sizing.splits.length === 0) return 'Pick at least one split.';
  const hasPreset = sizing.splits.some((key) => key !== CUSTOM_SPLIT);
  if (hasPreset && amount(sizing.total) === 0) return 'Enter a total for the splits you picked.';
  if (sizing.splits.includes(CUSTOM_SPLIT)
    && (amount(sizing.customCall) === 0 || amount(sizing.customPut) === 0)) {
    return 'Enter an amount for both sides of Custom.';
  }
  return null;
};

/** e.g. "25/75: Call $250 + Put $750". */
export const describeBudget = (budget) => (
  `${budget.label}: Call ${formatMoney(budget.callBudget)} + Put ${formatMoney(budget.putBudget)}`
);

/** The form section's collapsed summary. */
export const summarizeSizing = (sizing) => {
  if (!sizing.enabled) return '1 contract each';
  const budgets = resolveBudgets(sizing);
  if (budgets.length === 0) return 'Needs an amount';
  if (budgets.length > 1) return `${budgets.length} splits`;
  const [only] = budgets;
  return only.key === CUSTOM_SPLIT
    ? `Custom, ${formatMoney(only.callBudget + only.putBudget)}`
    : `${formatMoney(only.callBudget + only.putBudget)}, ${only.label}`;
};

/** The extra sentence for the run bar once splits multiply the rows. */
export const describeSplits = (straddleCount, splitCount) => (
  splitCount > 1 && straddleCount > 0
    ? `${plural(straddleCount, 'straddle')} x ${splitCount} splits = ${straddleCount * splitCount} rows`
    : ''
);

/**
 * The most rows the tables can show. The API's limit counts contracts it has to
 * fetch; this one is about the browser, since these tables aren't virtualized.
 *
 * Set from a production build measured on 2026-11-02. A slider move updated the
 * table in 0.85s at 1,350 rows and 1.15s at 1,800, but adding a split took 2.5s and
 * 6s, because creating new rows is what gets disproportionately slower.
 */
export const MAX_DISPLAY_ROWS = 1500;

/**
 * The splits that fit under the display limit: at least one, and the rest in button
 * order. Whatever doesn't fit is reported by the control rather than silently lost.
 */
export const budgetsWithinLimit = (budgets, rowCount) => {
  const fit = Math.max(1, Math.floor(MAX_DISPLAY_ROWS / Math.max(rowCount, 1)));
  return budgets.slice(0, fit);
};

// ---------------------------------------------------------------------------
// Result filters
// ---------------------------------------------------------------------------

/** Multi-select filters, in display order. `field` is the row key each one reads. */
export const FILTER_FIELDS = [
  { key: 'tickers', field: 'Ticker', label: 'Ticker' },
  { key: 'structures', field: 'Structure', label: 'Structure' },
  { key: 'relations', field: 'Expiry Relation', label: 'Expiry' },
  { key: 'callTenors', field: 'Call Tenor', label: 'Call Tenor' },
  { key: 'putTenors', field: 'Put Tenor', label: 'Put Tenor' },
  { key: 'qualities', field: 'Quality', label: 'Liquidity' },
  // Rows only carry these two while a budget is set.
  { key: 'splits', field: 'Split', label: 'Split' },
  { key: 'budgets', field: 'Budget Fit', label: 'Budget' },
];

export const EMPTY_FILTERS = Object.fromEntries(FILTER_FIELDS.map(({ key }) => [key, []]));

/** An empty filter passes everything; several active filters must all pass. */
export const matchesFilters = (row, filters) => FILTER_FIELDS.every(({ key, field }) => {
  const selected = filters?.[key] || [];
  return selected.length === 0 || selected.includes(row[field]);
});

// ---------------------------------------------------------------------------
// Row labels
// ---------------------------------------------------------------------------

export const formatStrike = (strike) => {
  const num = parseFloat(strike);
  if (Number.isNaN(num)) return '--';
  return Number.isInteger(num) ? `$${num}` : `$${num.toFixed(2)}`;
};

/**
 * "12/02", or "01/21/28" outside the current year. Reads the string directly:
 * `new Date('YYYY-MM-DD')` parses as UTC, which is the previous day in US zones.
 */
export const formatShortDate = (expiration, currentYear = new Date().getFullYear()) => {
  const [year, month, day] = String(expiration || '').split('-');
  if (!year || !month || !day) return '--';
  const short = `${month}/${day}`;
  return Number(year) === currentYear ? short : `${short}/${year.slice(2)}`;
};

/** One leg on one line, e.g. "C $110 12/02 30d". */
export const formatLegLabel = (row, side, currentYear) => {
  const isCall = side === 'call';
  const prefix = isCall ? 'Call' : 'Put';
  return `${isCall ? 'C' : 'P'} ${formatStrike(row[`${prefix} Strike`])} `
    + `${formatShortDate(row[`${prefix} Expiration`], currentYear)} ${row[`${prefix} DTE`]}d`;
};

/** A signed % for a breakeven, e.g. "+9.9%". */
export const formatSignedPct = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '--';
  const num = Number(value);
  return `${num > 0 ? '+' : ''}${num.toFixed(1)}%`;
};
